import React, { useCallback, useEffect, useRef, useState } from "react";
import { GoogleMap, useJsApiLoader, MarkerF, CircleF } from "@react-google-maps/api";
import { Navigation, MapPin, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { colors } from "@/lib/theme";
import { Avatar, Spinner } from "@/components/peeps/ui";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function fmtDist(m) {
  if (m == null) return "locating…";
  if (m < 1000) return `${m} m away`;
  return `${(m / 1000).toFixed(m < 10000 ? 1 : 0)} km away`;
}
function fmtAgo(iso) {
  try {
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    return `${Math.floor(s / 3600)}h ago`;
  } catch { return ""; }
}

export function LiveLocationMap({ eventId, active, themeColor }) {
  const { isLoaded, loadError } = useJsApiLoader({ id: "peeps-maps", googleMapsApiKey: MAPS_KEY || "" });
  const [authFailed, setAuthFailed] = useState(false);
  const [data, setData] = useState(null);
  const [geoError, setGeoError] = useState(null);
  const [myPos, setMyPos] = useState(null);
  const watchId = useRef(null);
  const lastPush = useRef(0);

  // Google fires this global when the key isn't authorized for the Maps JS
  // product on this domain — catch it so we show our own fallback, not Google's
  // built-in "Oops! Something went wrong" panel.
  useEffect(() => {
    window.gm_authFailure = () => setAuthFailed(true);
  }, []);

  const mapUnavailable = !MAPS_KEY || loadError || authFailed;

  const fetchLocations = useCallback(async () => {
    try {
      setData(await api.getEventLocations(eventId));
    } catch {}
  }, [eventId]);

  // Watch my geolocation and push it while sharing is active.
  useEffect(() => {
    if (!active) return;
    if (!("geolocation" in navigator)) {
      setGeoError("Your browser can't share location.");
      return;
    }
    watchId.current = navigator.geolocation.watchPosition(
      async (pos) => {
        setGeoError(null);
        const { latitude: lat, longitude: lng } = pos.coords;
        setMyPos({ lat, lng });
        const now = Date.now();
        if (now - lastPush.current > 8000) {
          lastPush.current = now;
          try { await api.postEventLocation(eventId, lat, lng); await fetchLocations(); } catch {}
        }
      },
      (err) => setGeoError(err.code === 1 ? "Location permission denied — allow it in your browser to share." : "Couldn't get your location."),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
    );
    return () => { if (watchId.current != null) navigator.geolocation.clearWatch(watchId.current); };
  }, [active, eventId, fetchLocations]);

  // Poll for others.
  useEffect(() => {
    if (!active) return;
    fetchLocations();
    const t = setInterval(fetchLocations, 12000);
    return () => clearInterval(t);
  }, [active, fetchLocations]);

  if (!active) return null;

  const people = data?.people || [];
  const center = myPos || data?.center || null;

  return (
    <div className="mt-3">
      {geoError && (
        <div className="flex items-center gap-2 rounded-xl px-3 py-2.5 mb-2" style={{ background: colors.warning + "22" }}>
          <AlertCircle size={15} color={colors.warning} />
          <span className="font-display text-[12.5px]" style={{ color: colors.warning }}>{geoError}</span>
        </div>
      )}

      {/* Map */}
      <div className="rounded-xl overflow-hidden" style={{ height: 240, background: colors.surfaceTertiary, border: `1px solid ${colors.border}` }}>
        {mapUnavailable ? (
          <div className="h-full flex flex-col items-center justify-center gap-1 px-4 text-center">
            <MapPin size={22} color={colors.onSurfaceTertiary} />
            <span className="font-display text-[12.5px]" style={{ color: colors.onSurfaceTertiary }}>Map unavailable — distances shown below.</span>
          </div>
        ) : !isLoaded || !center ? (
          <div className="h-full flex items-center justify-center gap-2">
            <Spinner style={{ width: 18, height: 18 }} />
            <span className="font-display text-[12.5px]" style={{ color: colors.onSurfaceTertiary }}>{center ? "Loading map…" : "Getting your location…"}</span>
          </div>
        ) : (
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={center}
            zoom={14}
            options={{ disableDefaultUI: true, zoomControl: true, gestureHandling: "greedy",
              styles: [{ elementType: "geometry", stylers: [{ color: "#1d1f2b" }] },
                { elementType: "labels.text.stroke", stylers: [{ color: "#1d1f2b" }] },
                { elementType: "labels.text.fill", stylers: [{ color: "#8a8ba0" }] },
                { featureType: "road", elementType: "geometry", stylers: [{ color: "#2c2d3a" }] },
                { featureType: "water", elementType: "geometry", stylers: [{ color: "#12131b" }] },
                { featureType: "poi", stylers: [{ visibility: "off" }] }] }}
          >
            {myPos && (
              <>
                <MarkerF position={myPos} label={{ text: "You", color: "#fff", fontSize: "11px", fontWeight: "700" }} />
                {data?.radius_threshold && (
                  <CircleF center={myPos} radius={data.radius_threshold}
                    options={{ strokeColor: themeColor, strokeOpacity: 0.5, strokeWeight: 1, fillColor: themeColor, fillOpacity: 0.1 }} />
                )}
              </>
            )}
            {people.map((p) => (
              <MarkerF key={p.id} position={{ lat: p.lat, lng: p.lng }}
                label={{ text: (p.display_name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase(), color: "#15161e", fontSize: "10px", fontWeight: "700" }} />
            ))}
          </GoogleMap>
        )}
      </div>

      {/* Who's sharing list */}
      <div className="mt-2 flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <Navigation size={13} color={themeColor} />
          <span className="font-display font-medium text-[12px]" style={{ color: colors.onSurfaceTertiary }}>
            {people.length === 0 ? "No one else is sharing right now" : `${people.length} sharing near you`}
          </span>
        </div>
        {people.map((p) => (
          <div key={p.id} data-testid={`nearby-${p.id}`} className="flex items-center gap-2.5 rounded-xl p-2" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
            <Avatar name={p.display_name} uri={p.profile_photo_url} size={30} />
            <span className="flex-1 font-display font-medium text-[13.5px] truncate" style={{ color: colors.onSurface }}>{p.display_name}</span>
            <span className="font-mono2 font-medium text-[12px]" style={{ color: themeColor }}>{fmtDist(p.distance_m)}</span>
            <span className="font-display text-[11px]" style={{ color: colors.faint }}>{fmtAgo(p.updated_at)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
